import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const PADDING = 8;
const GAP = 8;

type Placement = 'above' | 'below';

export const InfoTooltip: React.FC<{ content: React.ReactNode; children?: React.ReactNode; triggerClassName?: string }> = ({ content, children, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; placement: Placement } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const useCustomTrigger = children != null;

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleTriggerEnter = () => {
    clearCloseTimeout();
    setOpen(true);
  };

  const handleTriggerLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      setPosition(null);
    }, 150);
  };

  const handleTooltipEnter = () => clearCloseTimeout();
  const handleTooltipLeave = () => {
    setOpen(false);
    setPosition(null);
  };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tw = tooltipEl.offsetWidth;
    const th = tooltipEl.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer above: center horizontally under the trigger center
    let top = triggerRect.top - th - GAP;
    let left = triggerRect.left + triggerRect.width / 2 - tw / 2;
    let placement: Placement = 'above';

    if (top < PADDING) {
      // Not enough space above -> show below
      top = triggerRect.bottom + GAP;
      placement = 'below';
    }
    if (top + th > vh - PADDING) {
      top = vh - th - PADDING;
    }
    if (top < PADDING) {
      top = PADDING;
    }

    if (left < PADDING) {
      left = PADDING;
    }
    if (left + tw > vw - PADDING) {
      left = vw - tw - PADDING;
    }

    setPosition({ top, left, placement });
  }, [open]);

  const tooltipContent = open && (
    <div
      ref={tooltipRef}
      role="tooltip"
      className="fixed z-[9999] w-80 max-w-[min(calc(100vw-2rem),24rem)] rounded-lg bg-gray-900 text-white text-sm shadow-lg py-2 px-3 whitespace-normal"
      style={{
        left: position ? position.left : 0,
        top: position ? position.top : 0,
        visibility: position ? 'visible' : 'hidden',
      }}
      onMouseEnter={handleTooltipEnter}
      onMouseLeave={handleTooltipLeave}
    >
      <div>{content}</div>
      {/* Arrow pointing to trigger - centered at bottom (above) or top (below) */}
      {position && (
        <div
          className="absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent"
          style={
            position.placement === 'above'
              ? { bottom: -6, borderTop: '6px solid rgb(17 24 39)' }
              : { top: -6, borderBottom: '6px solid rgb(17 24 39)' }
          }
        />
      )}
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        className={useCustomTrigger ? `inline-flex ${triggerClassName ?? ''}`.trim() : 'inline-flex flex-shrink-0 cursor-help'}
        onMouseEnter={handleTriggerEnter}
        onMouseLeave={handleTriggerLeave}
        aria-describedby={open ? 'info-tooltip' : undefined}
      >
        {useCustomTrigger ? children : <i className="bi bi-info-circle text-[#0B4778] text-lg hover:opacity-80" aria-hidden />}
      </span>
      {createPortal(tooltipContent, document.body)}
    </>
  );
};
